import FileCopyIcon from '@mui/icons-material/FileCopy'
import { IconButton, SvgIconTypeMap, SxProps, Tooltip } from '@mui/material'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import { ComponentProps, useState } from 'react'

type TProps = {
  textToCopy?: string
  icon?: OverridableComponent<SvgIconTypeMap<object, 'svg'>> & {
    muiName: string
  }
  iconButtonProps?: Omit<ComponentProps<typeof IconButton>, 'onClick'>
  iconSxProps?: SxProps
  sx?: SxProps
  tooltipProps?: Omit<
    ComponentProps<typeof Tooltip>,
    'sx' | 'title' | 'children'
  >
  tooltipTitle?: string
}

function CopyToClipboardButton({
  textToCopy = '',
  icon: Icon = FileCopyIcon,
  iconButtonProps = {},
  iconSxProps: fileIconSxProps = {},
  sx = {},
  tooltipProps = {},
  tooltipTitle = 'Copy to clipboard',
}: TProps) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const ariaLabel =
    (iconButtonProps &&
      (iconButtonProps as { 'aria-label'?: string })['aria-label']) ||
    (copied ? 'Copied!' : 'Copy to clipboard')

  return (
    <Tooltip
      title={copied ? 'Copied!' : tooltipTitle}
      placement="right-start"
      sx={{ ...sx }}
      {...tooltipProps}
    >
      <IconButton
        onClick={handleCopy}
        aria-label={ariaLabel}
        // ensure at least 44x44 tap target while allowing overrides
        sx={{ width: 44, height: 44, ...(iconButtonProps?.sx as SxProps) }}
        {...iconButtonProps}
      >
        <Icon
          sx={{
            fontSize: 'medium',
            ...fileIconSxProps,
          }}
        />
      </IconButton>
    </Tooltip>
  )
}

export default CopyToClipboardButton
